import { ButtonLight, ButtonPrimary } from 'components/Button'
import { BlueCard } from 'components/Card'
import { AutoColumn, ColumnCenter } from 'components/Column'
import { RowBetween } from 'components/Row'
import { useV3Factory } from 'hooks/useContract'
import { StepReview } from 'pages/AddLiquidity/styled'
import { Dots } from 'pages/Pool/styleds'
import React, { Dispatch, SetStateAction, useState } from 'react'
import { TYPE } from 'theme'
import { API_GASLESS_BASE_URL } from 'constants/links'
import { generateSignature } from 'utils/cryptoUtils'
import axios from 'axios'
import { useActiveWeb3React } from 'hooks'
import { Currency, Token, WETH9 } from '@uniswap/sdk-core'
import { CURRENT_CHAIN } from '@hyperswap/addresses'
import { v4 as uuidv4 } from 'uuid'
import { FeeAmount } from '@uniswap/v3-sdk'

interface Step2Props {
  activeStep: number
  pairDoesNotExists: boolean
  successCreate: boolean
  setSuccessCreate: Dispatch<SetStateAction<boolean>>
  step2Validate: boolean
  handleStep: (step: number, force?: boolean) => void
  currencyA: Currency | null | undefined
  currencyB: Currency | null | undefined
  feeAmount: FeeAmount | undefined
}
const Step2: React.FC<Step2Props> = ({
  activeStep,
  pairDoesNotExists,
  successCreate,
  setSuccessCreate,
  step2Validate,
  handleStep,
  currencyA,
  currencyB,
  feeAmount,
}) => {
  const [loadingCreate, setLoadingCreate] = useState(false)
  const [errorCreate, setErrorCreate] = useState('')
  const factoryContract = useV3Factory()
  const { account, chainId, library } = useActiveWeb3React()

  async function onCreate() {
    if (!chainId || !library || !account || !factoryContract) return
    setLoadingCreate(true)
    const currencyAAddress = currencyA instanceof Token ? currencyA.address : WETH9[CURRENT_CHAIN].address
    const currencyBAddress = currencyB instanceof Token ? currencyB.address : WETH9[CURRENT_CHAIN].address

    const currencyASymbol = currencyA?.symbol
    const currencyBSymbol = currencyB?.symbol

    const timestamp = Math.floor(Date.now() / 1000)
    const message = `Please sign this message to create pair ${currencyASymbol}-${currencyBSymbol}. Timestamp: ${timestamp}`

    try {
      const signer = library.getSigner()
      const signature = await signer.signMessage(message)
      const uuid = uuidv4()
      setErrorCreate('')
      setSuccessCreate(false)

      const checkStatus = async () => {
        factoryContract.on('PoolCreated', (token0, token1, fee, poolAddress) => {
          if (
            ((token0.toLowerCase() === currencyAAddress.toLowerCase() &&
              token1.toLowerCase() === currencyBAddress.toLowerCase()) ||
              (token0.toLowerCase() === currencyBAddress.toLowerCase() &&
                token1.toLowerCase() === currencyAAddress.toLowerCase())) &&
            fee === feeAmount
          ) {
            console.log(`LP address: ${poolAddress}`)
            factoryContract.removeAllListeners('PoolCreated')
            setLoadingCreate(false)
            setSuccessCreate(true)
            setErrorCreate('')
            handleStep(3, true)
          }
        })
      }

      const body = JSON.stringify({
        taskId: uuid,
        address: account,
        signature: signature,
        message: message,
        tokenA: currencyAAddress,
        tokenB: currencyBAddress,
        version: 'v3',
        fee: `${feeAmount}`,
      })

      const config = {
        method: 'post',
        url: `${API_GASLESS_BASE_URL}/api/relay-create-pair`,
        headers: {
          'x-signature': generateSignature(message),
          'Content-Type': 'application/json',
        },
        data: body,
      }

      axios
        .request(config)
        .then(async (response) => {
          console.log(response.data)
          if (response.data.success === true) {
            await checkStatus()
          } else {
            setLoadingCreate(false)
            setSuccessCreate(false)
            setErrorCreate('An error has occurred. Please try again.')
            console.log(response)
          }
        })
        .catch((error: any) => {
          setLoadingCreate(false)
          setSuccessCreate(false)
          setErrorCreate('An error has occurred. Please try again.')
          console.error(error)
        })
    } catch (error) {
      setLoadingCreate(false)
      setSuccessCreate(false)
      console.error('Error sending transaction:', error)
    }
  }

  return (
    <>
      {activeStep === 2 && (
        <>
          {pairDoesNotExists ? (
            <>
              <ColumnCenter>
                <BlueCard style={{ maxWidth: '500px' }}>
                  <AutoColumn gap="10px">
                    <TYPE.link fontWeight={600} color={'primaryText1'}>
                      You are the first liquidity library.
                    </TYPE.link>
                  </AutoColumn>
                </BlueCard>
              </ColumnCenter>
            </>
          ) : (
            <></>
          )}
          <AutoColumn gap="md" style={{ maxWidth: '500px' }}>
            <RowBetween paddingBottom="20px">
              <TYPE.label>Initialize the Pool</TYPE.label>
            </RowBetween>
            {pairDoesNotExists ? (
              <TYPE.link fontWeight={400} color={'text2'}>
                This pool does not exist yet on the protocol. To initialize it, you need to sign a message with your
                wallet. This signature is completely gasless (no transaction fees) and allows the pair to be created on
                the network.
              </TYPE.link>
            ) : (
              <TYPE.link fontWeight={400} color={'text2'}>
                This pool has already been initialized on the protocol. This step is not required, and you can proceed
                to the next step.
              </TYPE.link>
            )}
          </AutoColumn>
          <AutoColumn gap="md" style={{ maxWidth: '500px' }}>
            <ButtonPrimary disabled={!pairDoesNotExists || loadingCreate || successCreate} onClick={() => onCreate()}>
              {loadingCreate ? <Dots>Loading</Dots> : 'Sign to initialize the pool'}
            </ButtonPrimary>
            <ButtonLight disabled={!step2Validate} onClick={() => handleStep(3)}>
              Continue
            </ButtonLight>

            {errorCreate.length > 0 && !step2Validate && (
              <TYPE.link fontWeight={400} color={'error'}>
                {errorCreate}
              </TYPE.link>
            )}
          </AutoColumn>
        </>
      )}

      {activeStep > 2 && step2Validate && (
        <StepReview>
          <span className="title">Step 3</span>
          <div className="value">Pool Has Been Initialized</div>
          <div></div>
        </StepReview>
      )}
    </>
  )
}
export default React.memo(Step2)
